'use client';

import React from 'react';
import { Check, ArrowRight, Clock } from 'lucide-react';

interface Props {
  onNext: () => void;
}

const benefits = [
  { id: 'b-stem', text: 'Re-activate dormant stem cells' },
  { id: 'b-energy', text: 'Improved energy and vitality' },
  { id: 'b-exercise', text: 'Enhanced exercise performance' },
  { id: 'b-sleep', text: 'Better sleep quality' },
  { id: 'b-recovery', text: 'Faster recovery from physical activity' },
  { id: 'b-nodrugs', text: 'No drugs, no stimulants, no chemicals' },
  { id: 'b-safe', text: 'Safe for daily wear — up to 12 hours' },
  { id: 'b-noninvasive', text: 'Non-invasive, non-transdermal technology' },
];

export default function Slide4Product({ onNext }: Props) {
  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 50% at 70% 50%, rgba(0,168,255,0.09) 0%, transparent 65%)',
        }}
      />

      <div className="max-w-5xl w-full mx-auto fade-in">
        <div className="text-center mb-12">
          <p className="text-primary text-sm font-semibold tracking-widest uppercase mb-3">
            The Product
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground leading-tight mb-5">
            Introducing{' '}
            <span className="gradient-text">LifeWave X39®</span>
          </h2>
          <p className="text-secondary-foreground text-lg max-w-2xl mx-auto">
            The world&apos;s first product designed to activate your body&apos;s own stem cells
            using your own light energy — nothing enters the body.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-12">
          {/* Product Visual */}
          <div className="flex justify-center">
            <div className="relative w-52 h-52 flex items-center justify-center">
              {/* Outer glow rings only */}
              {[0, 1, 2].map((i) => (
                <div
                  key={`ring-${i}`}
                  className="absolute rounded-full border border-primary/15"
                  style={{
                    inset: `-${(i + 1) * 20}px`,
                    animation: `pulseRing 3s ease-out ${i * 0.9}s infinite`,
                  }}
                />
              ))}
              <div className="w-52 h-52 rounded-full border border-primary/20 flex flex-col items-center justify-center relative z-10 float-anim"
                style={{ background: 'rgba(0,168,255,0.06)' }}>
                <span className="text-primary font-extrabold text-3xl tracking-widest">X39</span>
                <span className="text-muted-foreground text-xs mt-1 tracking-widest">LIFEWAVE®</span>
                <span className="text-muted-foreground/60 text-xs tracking-wider">PHOTOTHERAPY</span>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {benefits.map((b) => (
                <div key={b.id} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={11} className="text-primary" />
                  </div>
                  <span className="text-secondary-foreground text-sm leading-snug">{b.text}</span>
                </div>
              ))}
            </div>

            <div className="glass-card p-4 flex items-start gap-3 border-primary/20 bg-primary/5">
              <Clock size={18} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-foreground font-semibold text-sm mb-0.5">Simple Daily Use</p>
                <p className="text-secondary-foreground text-xs leading-relaxed">
                  Apply to clean dry skin each morning. Wear for up to 12 hours. Remove and discard. Stay hydrated.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {[
            { id: 'stat-countries', value: '100+', label: 'Countries' },
            { id: 'stat-studies', value: '80+', label: 'Clinical Studies' },
            { id: 'stat-years', value: '20+', label: 'Years Research' },
            { id: 'stat-patents', value: '200+', label: 'Global Patents' },
          ].map((stat) => (
            <div key={stat.id} className="glass-card p-4 text-center">
              <p className="gradient-text text-2xl font-extrabold tabular-nums">{stat.value}</p>
              <p className="text-muted-foreground text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Testimonial Videos */}
        <div className="mb-10">
          <div className="text-center mb-6">
            <p className="text-primary text-sm font-semibold tracking-widest uppercase mb-2">Real Results</p>
            <h3 className="text-2xl font-bold text-foreground">What People Are Saying</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { id: 'v1', videoId: '1118429044' },
              { id: 'v2', videoId: '1118429946' },
              { id: 'v3', videoId: '1118433583' },
              { id: 'v4', videoId: '1118434636' },
              { id: 'v5', videoId: '1118430693' },
            ].map((v) => (
              <div key={v.id} className="glass-card overflow-hidden">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={`https://player.vimeo.com/video/${v.videoId}`}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={`Testimonial video ${v.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center">
          <button onClick={onNext} className="btn-primary inline-flex items-center gap-2">
            See Pricing Options
            <ArrowRight size={18} />
          </button>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 pt-6 border-t border-white/10">
          <p className="text-muted-foreground text-xs leading-relaxed text-center max-w-3xl mx-auto">
            Disclaimer: Our patches are based on the theory of phototherapy. The patches are not proven based on conventional medicine standards and should not be used in place of medical care. Our products are not intended to diagnose, treat, cure or prevent any disease or medical condition. The content presented is in summary form, is general in nature and provided for informational purposes only. We assume no liability or responsibility for damage or injury to persons or property arising from any use of any product, information, ideas or instruction contained in the materials provided to you.
          </p>
        </div>
      </div>
    </div>
  );
}